import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import KnowledgeProvidersPanel from "./knowledge/KnowledgeProvidersPanel";
import { useFormContext } from "react-hook-form";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useOrganization } from "@/App";
import { toast } from "@/components/ui/use-toast";
import {
  Upload,
  FileText,
  Trash2,
  Search,
  InfoIcon,
  RefreshCw,
  FileUp,
  Link,
  Plus,
  ExternalLink,
  Globe,
  Loader2,
} from "lucide-react";

interface KnowledgeDocument {
  id: string;
  filename: string;
  original_filename: string;
  file_path: string;
  file_type: string;
  file_size?: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message?: string;
  chunk_count?: number;
  created_at: string;
  updated_at: string;
  metadata?: any;
}

export default function KnowledgeBaseTab({ assistantId }: { assistantId?: string | null }) {
  const { currentOrganization } = useOrganization();
  const form = useFormContext();
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [urlInput, setUrlInput] = useState("");
  const [uploadMethod, setUploadMethod] = useState<'file' | 'url'>('file');
  const [documentToDelete, setDocumentToDelete] = useState<KnowledgeDocument | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [searchEnabled, setSearchEnabled] = useState(form.getValues('knowledgeBaseEnabled') || false);

  // Fetch documents when component mounts or assistant changes
  useEffect(() => {
    if (assistantId) {
      fetchDocuments();
    }
  }, [assistantId]);

  const fetchDocuments = async () => {
    if (!assistantId || !currentOrganization?.id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('knowledge_documents')
        .select('*')
        .eq('assistant_id', assistantId)
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: "Error",
        description: "Failed to fetch knowledge base documents",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = [
        'application/pdf',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/markdown',
        'text/csv'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a PDF, TXT, DOC, DOCX, MD, or CSV file",
          variant: "destructive",
        });
        return;
      }

      // 10MB limit
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload a file smaller than 10MB",
          variant: "destructive",
        });
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!assistantId || !currentOrganization?.id) {
      toast({
        title: "Error",
        description: "Assistant must be saved before adding knowledge base documents",
        variant: "destructive",
      });
      return;
    }

    if (uploadMethod === 'file' && !selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload",
        variant: "destructive",
      });
      return;
    }

    if (uploadMethod === 'url' && !urlInput) {
      toast({
        title: "No URL provided",
        description: "Please enter a URL to process",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    
    try {
      if (uploadMethod === 'file' && selectedFile) {
        // Upload file to storage
        const fileExt = selectedFile.name.split('.').pop();
        const fileName = `${currentOrganization.id}/${assistantId}/${Date.now()}_${selectedFile.name}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('assistant-knowledge-bases')
          .upload(fileName, selectedFile);

        if (uploadError) throw uploadError;

        // Call edge function to process the file
        const { data, error } = await supabase.functions.invoke('process-knowledge-document', {
          body: {
            assistant_id: assistantId,
            organization_id: currentOrganization.id,
            file_path: uploadData.path,
            file_name: selectedFile.name,
            file_type: selectedFile.type,
            file_size: selectedFile.size,
          },
        });

        if (error) throw error;
        
        toast({
          title: "File uploaded successfully",
          description: "Processing document in the background...",
        });
      } else if (uploadMethod === 'url') {
        // Process URL
        const { data, error } = await supabase.functions.invoke('process-knowledge-document', {
          body: {
            assistant_id: assistantId,
            organization_id: currentOrganization.id,
            source_url: urlInput,
          },
        });

        if (error) throw error;
        
        toast({
          title: "URL submitted successfully",
          description: "Processing webpage in the background...",
        });
      }

      // Clear inputs and refresh documents
      setSelectedFile(null);
      setUrlInput("");
      if (uploadMethod === 'file') {
        const fileInput = document.getElementById('file-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }
      
      // Refresh the documents list
      await fetchDocuments();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload document",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (document: KnowledgeDocument) => {
    if (!currentOrganization?.id) return;

    try {
      const { error } = await supabase.functions.invoke('delete-knowledge-document', {
        body: {
          document_id: document.id,
          organization_id: currentOrganization.id,
        },
      });

      if (error) throw error;

      toast({
        title: "Document deleted",
        description: `"${document.original_filename}" has been removed from the knowledge base`,
      });

      // Refresh the documents list
      await fetchDocuments();
    } catch (error: any) {
      console.error('Delete error:', error);
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete document",
        variant: "destructive",
      });
    } finally {
      setDocumentToDelete(null);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-good text-background">Ready</Badge>;
      case 'processing':
        return <Badge className="bg-mid text-background">Processing</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'pending':
        return <Badge className="bg-primary text-primary-foreground">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Enable/Disable Knowledge Base */}
      <Card>
        <CardHeader>
          <CardTitle>Knowledge Base Settings</CardTitle>
          <CardDescription>
            Control whether the assistant can use the search_knowledge tool to access uploaded documents
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormField
            control={form.control}
            name="knowledgeBaseEnabled"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Enable Knowledge Base Tool</FormLabel>
                  <FormDescription>
                    Allow the assistant to use the search_knowledge tool to find information from uploaded documents when needed
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value || false}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      // Same switch as Configuration > Knowledge Base Search; keep both flags equal.
                      form.setValue('enableSearchKnowledge', checked, { shouldDirty: true });
                      setSearchEnabled(checked);
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      <Tabs defaultValue="documents">
      <TabsList>
        <TabsTrigger value="documents">Documents</TabsTrigger>
        <TabsTrigger value="processing">Processing & Search</TabsTrigger>
        <TabsTrigger value="providers">Providers</TabsTrigger>
      </TabsList>

      <TabsContent value="documents" className="mt-6 space-y-6">
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle>Add Documents</CardTitle>
          <CardDescription>
            Upload documents or provide URLs to build your assistant's knowledge base
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!assistantId && (
            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                Please save the assistant first before adding knowledge base documents.
              </AlertDescription>
            </Alert>
          )}

          {/* Upload Method Toggle */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant={uploadMethod === 'file' ? 'default' : 'outline'}
              onClick={() => setUploadMethod('file')}
              className="flex-1"
            >
              <FileUp className="mr-2 h-4 w-4" />
              Upload File
            </Button>
            <Button
              type="button"
              variant={uploadMethod === 'url' ? 'default' : 'outline'}
              onClick={() => setUploadMethod('url')}
              className="flex-1"
            >
              <Globe className="mr-2 h-4 w-4" />
              Add URL
            </Button>
          </div>

          {uploadMethod === 'file' ? (
            <div className="space-y-4">
              <div className="grid w-full items-center gap-1.5">
                <FormLabel htmlFor="file-upload">Select File</FormLabel>
                <Input
                  id="file-upload"
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.txt,.doc,.docx,.md,.csv"
                  disabled={!assistantId || isUploading}
                />
                <FormDescription>
                  Supported formats: PDF, TXT, DOC, DOCX, MD, CSV (Max 10MB)
                </FormDescription>
              </div>
              
              {selectedFile && (
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm">{selectedFile.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({formatFileSize(selectedFile.size)})
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedFile(null);
                      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
                      if (fileInput) fileInput.value = '';
                    }}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid w-full items-center gap-1.5">
                <FormLabel htmlFor="url-input">Website URL</FormLabel>
                <Input
                  id="url-input"
                  type="url"
                  placeholder="https://example.com/documentation"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  disabled={!assistantId || isUploading}
                />
                <FormDescription>
                  Enter a URL to extract and index its content
                </FormDescription>
              </div>
            </div>
          )}

          <Button
            type="button"
            onClick={handleUpload}
            disabled={!assistantId || isUploading || (uploadMethod === 'file' ? !selectedFile : !urlInput)}
            className="w-full"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                {uploadMethod === 'file' ? 'Upload Document' : 'Add URL'}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Knowledge Base Documents</CardTitle>
              <CardDescription>
                Manage documents in your assistant's knowledge base
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={fetchDocuments}
              disabled={isLoading}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                No documents uploaded yet
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Upload documents to build your knowledge base
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Chunks</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm">{doc.original_filename}</p>
                            {doc.error_message && (
                              <p className="text-xs text-destructive mt-1">
                                {doc.error_message}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {doc.original_filename.split('.').pop()?.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                      <TableCell>{getStatusBadge(doc.status)}</TableCell>
                      <TableCell>
                        {doc.chunk_count ? (
                          <span className="text-sm">{doc.chunk_count}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(doc.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDocumentToDelete(doc)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {documents.length > 0 && (
            <div className="mt-4 text-sm text-muted-foreground">
              Total documents: {documents.length} | 
              Total chunks: {documents.reduce((acc, doc) => acc + (doc.chunk_count || 0), 0)}
            </div>
          )}
        </CardContent>
      </Card>

      </TabsContent>

      <TabsContent value="processing" className="mt-6 space-y-6">
      {/* Search and Processing Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Processing & Search Settings</CardTitle>
          <CardDescription>
            Configure how documents are processed into chunks and how the knowledge base search behaves
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="knowledgeBaseChunkSize"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Chunk Size (tokens)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="100"
                    max="4000"
                    placeholder="1000"
                    {...field}
                    onChange={e => field.onChange(parseInt(e.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  Maximum number of tokens per text chunk (100-4000). Larger chunks preserve more context but may be less precise.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="knowledgeBaseChunkOverlap"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Chunk Overlap (tokens)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    max="1000"
                    placeholder="200"
                    {...field}
                    onChange={e => field.onChange(parseInt(e.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  Number of tokens to overlap between chunks (0-1000). Overlap helps maintain context continuity.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="knowledgeBaseSearchThreshold"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Similarity Threshold</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    placeholder="0.7"
                    {...field}
                    onChange={e => field.onChange(parseFloat(e.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  Minimum similarity score for search results (0.0-1.0). Higher values return more relevant but fewer results.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="knowledgeBaseMaxResults"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Maximum Results</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    placeholder="3"
                    {...field}
                    onChange={e => field.onChange(parseInt(e.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  Maximum number of search results to include in the context (1-10)
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="knowledgeBasePromptTemplate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Search Result Template</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Here is relevant information from the knowledge base: {results}"
                    className="min-h-[100px] max-h-[60vh] overflow-y-auto"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Template for injecting search results into the conversation. Use {`{results}`} to insert the search results.
                </FormDescription>
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      </TabsContent>

      <TabsContent value="providers" className="mt-6">
        <KnowledgeProvidersPanel />
      </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!documentToDelete} onOpenChange={() => setDocumentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{documentToDelete?.original_filename}"? This action cannot be undone and will remove all indexed content from this document.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => documentToDelete && handleDelete(documentToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}